import type { CellValueChangedEvent, ColDef, GridApi } from 'ag-grid-community'
import { AllCommunityModule, createGrid, ModuleRegistry } from 'ag-grid-community'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { insertHtml } from '../../api/word/word-service'
import { WORKING_DOCUMENT_ID } from '../../constants/files'
import { useGetFileSectionContent, useNotification } from '../../hooks'
import {
  buildColumnDefsAndRowData,
  extractTitleAndFooter,
  matchesSmartFilter,
} from './filtering-utils'
import { buildTableInsertHtml, parseHtmlTableToValues } from './html-utils'

ModuleRegistry.registerModules([AllCommunityModule])

interface TableSnapshot {
  columnDefs: ColDef[]
  rowData: Record<string, string>[]
  tableTitle: string
  tableFooter: string
}

/** Deep-copy a snapshot's columnDefs/rowData so callers can mutate one side independently. */
function cloneSnapshot(s: TableSnapshot): TableSnapshot {
  return {
    columnDefs: s.columnDefs.map((c) => ({ ...c })),
    rowData: s.rowData.map((r) => ({ ...r })),
    tableTitle: s.tableTitle,
    tableFooter: s.tableFooter,
  }
}

export interface TableEditorState {
  /** Attach to the ag-grid container div via ref={gridContainerRef}. */
  gridContainerRef: React.RefObject<HTMLDivElement | null>
  canInsert: boolean
  canUndo: boolean
  hasChanges: boolean
  /** True when there are data changes, active column filters, or active sorting. */
  canReset: boolean
  /** True when at least one row is checked. */
  canDeleteRows: boolean
  /** True when a data cell (not header or checkbox column) is focused. */
  canDeleteColumn: boolean
  tableTitle: string
  tableFooter: string
  statusMessage: string
  isTableDataLoading: boolean
  isTableDataError: boolean
  /** Sets tableTitle and marks the editor as having unsaved changes. */
  updateTableTitle: (value: string) => void
  /** Sets tableFooter and marks the editor as having unsaved changes. */
  updateTableFooter: (value: string) => void
  handleUndo: () => void
  handleResetTable: () => void
  handleDeleteSelected: () => void
  handleDeleteColumn: () => void
  insertFilteredTableToDocument: () => Promise<void>
}

export function useTableEditor(fileId?: string, sectionId?: string): TableEditorState {
  const { t } = useTranslation()
  const { notify } = useNotification()

  const {
    data: filepoolTableData,
    isLoading: isTableDataLoading,
    isError: isTableDataError,
  } = useGetFileSectionContent(
    { fileId: fileId ?? '', sectionId: sectionId ?? '' },
    !!fileId && !!sectionId && fileId !== WORKING_DOCUMENT_ID
  )

  // TODO (working-document flow - #4435): when fileId === WORKING_DOCUMENT_ID, fetch the table HTML
  // via a Word API hook (getRange().getHtml() for the given section/table ID), then feed it
  // through the same parseHtmlTableToValues → extractTitleAndFooter → applyLoadedData pipeline
  // used below for filepool. Set sourceTableHtml from that result so buildFilteredHtmlFromSource
  // runs on insert. Styling differences (if any) can be handled at insert time.

  /** Grid values parsed from the fetched filepool HTML; null when unparseable. */
  const tableFilterValues = useMemo(
    () => (filepoolTableData ? parseHtmlTableToValues(filepoolTableData.html) : null),
    [filepoolTableData]
  )

  const gridContainerRef = useRef<HTMLDivElement>(null)
  const gridApiRef = useRef<GridApi<Record<string, string>> | null>(null)
  /** Track which DOM element the grid was last attached to so we can detect remounts. */
  const gridContainerElRef = useRef<HTMLDivElement | null>(null)
  /** When true, the next onSelectionChanged fires as a side effect of an action (e.g. delete)
   * rather than a user gesture — skip clearing actionMessage in that case. */
  const skipActionMessageClearRef = useRef(false)
  const originalDataRef = useRef<TableSnapshot | null>(null)
  const undoStackRef = useRef<TableSnapshot[]>([])
  const skipGridSyncRef = useRef(false)

  const [columnDefs, setColumnDefs] = useState<ColDef[]>([])
  const [rowData, setRowData] = useState<Record<string, string>[]>([])
  const [tableTitle, setTableTitle] = useState('')
  const [tableFooter, setTableFooter] = useState('')
  /** Mirror of latest state so grid event handlers can read current values synchronously. */
  const liveStateRef = useRef<TableSnapshot>({ columnDefs, rowData, tableTitle, tableFooter })
  liveStateRef.current = { columnDefs, rowData, tableTitle, tableFooter }

  /** Deep-copy of the current live state, safe to push onto the undo stack. */
  function snapshotLive(): TableSnapshot {
    return cloneSnapshot(liveStateRef.current)
  }

  /** Apply a snapshot to grid state, deep-copying so the source snapshot stays independent. */
  function applyValues(snapshot: TableSnapshot) {
    const copy = cloneSnapshot(snapshot)
    setColumnDefs(copy.columnDefs)
    setRowData(copy.rowData)
    setTableTitle(copy.tableTitle)
    setTableFooter(copy.tableFooter)
  }
  const [sourceTableHtml, setSourceTableHtml] = useState<string | null>(null)
  const [actionMessage, setActionMessage] = useState('')
  const [hasChanges, setHasChanges] = useState(false)
  const [hasActiveFilter, setHasActiveFilter] = useState(false)
  const [hasActiveSort, setHasActiveSort] = useState(false)
  const [canUndo, setCanUndo] = useState(false)
  const [selectedCount, setSelectedCount] = useState(0)
  const [focusedColId, setFocusedColId] = useState<string | null>(null)

  const applyLoadedData = useCallback(
    (nextData: {
      columnDefs: ColDef[]
      rowData: Record<string, string>[]
      title?: string
      footer?: string
    }) => {
      setColumnDefs(nextData.columnDefs)
      /** Normalise whitespace before opening brackets: "4 ( 65)" → "4 (65)" */
      const normalised = nextData.rowData.map((row) => {
        const cleaned: Record<string, string> = {}
        for (const [key, val] of Object.entries(row)) {
          cleaned[key] = val.replace(/\s+\(\s+/g, ' (')
        }
        return cleaned
      })
      setRowData(normalised)
      setTableTitle(nextData.title ?? '')
      setTableFooter(nextData.footer ?? '')
      originalDataRef.current = cloneSnapshot({
        columnDefs: nextData.columnDefs,
        rowData: normalised,
        tableTitle: nextData.title ?? '',
        tableFooter: nextData.footer ?? '',
      })
      undoStackRef.current = []
      setCanUndo(false)
      setHasChanges(false)
      setHasActiveFilter(false)
      setHasActiveSort(false)
    },
    []
  )

  /** Populate the grid whenever the filepool query resolves. */
  useEffect(() => {
    if (!tableFilterValues) return
    // Preserve raw HTML so buildFilteredHtmlFromSource runs on insert, keeping original markup.
    setSourceTableHtml(filepoolTableData?.html ?? null)

    const { title, footer, bodyValues } = extractTitleAndFooter(tableFilterValues)
    const nextData = buildColumnDefsAndRowData(bodyValues)
    applyLoadedData({ ...nextData, title, footer })
    const message = t('tables.loadedStatus', {
      rows: nextData.rowData.length,
      columns: nextData.columnDefs.length,
    })
    setActionMessage(message)
  }, [tableFilterValues, filepoolTableData, t, notify, applyLoadedData])

  /** Read current grid state into React state (after drag). */
  const syncFromGrid = useCallback(() => {
    if (!gridApiRef.current) return
    undoStackRef.current.push(snapshotLive())
    setCanUndo(true)
    const newRowData: Record<string, string>[] = []
    gridApiRef.current.forEachNode((node) => {
      if (node.data) newRowData.push(node.data)
    })
    skipGridSyncRef.current = true
    setRowData(newRowData)
    setHasChanges(true)
  }, [])

  /**
   * ag-grid mutates node.data IN PLACE before firing onCellValueChanged, and React rowData
   * shares those object references — so liveState already holds the NEW value. If we snapshot
   * as-is, undo restores the already-edited value and appears to do nothing. Rebuild the
   * pre-edit snapshot by reverting just the changed cell to its old value.
   */
  const handleCellValueChanged = useCallback(
    (event: CellValueChangedEvent<Record<string, string>>) => {
      if (!gridApiRef.current) return
      const field = event.column.getColId()
      const preEdit = cloneSnapshot(liveStateRef.current)
      const editedIndex = liveStateRef.current.rowData.indexOf(event.data)
      if (editedIndex !== -1) {
        preEdit.rowData[editedIndex][field] = (event.oldValue as string | undefined) ?? ''
      }
      undoStackRef.current.push(preEdit)
      setCanUndo(true)

      const newRowData: Record<string, string>[] = []
      gridApiRef.current.forEachNode((node) => {
        if (node.data) newRowData.push(node.data)
      })
      skipGridSyncRef.current = true
      setRowData(newRowData)
      setHasChanges(true)
    },
    []
  )

  function pushUndo() {
    undoStackRef.current.push(snapshotLive())
    setCanUndo(true)
  }

  function handleDeleteSelected() {
    if (!gridApiRef.current) return
    const selectedRows = gridApiRef.current.getSelectedRows()
    if (selectedRows.length === 0) {
      notify({ text: t('tables.noRowsSelected'), isSuccess: false })
      return
    }
    pushUndo()
    const selectedSet = new Set(selectedRows)
    skipActionMessageClearRef.current = true
    setRowData((prev) => prev.filter((row) => !selectedSet.has(row)))
    setHasChanges(true)
    setSelectedCount(0)
    setActionMessage(t('tables.rowsDeleted', { count: selectedRows.length }))
  }

  function handleResetTable() {
    if (!originalDataRef.current) return
    pushUndo()
    applyValues(originalDataRef.current)
    gridApiRef.current?.setFilterModel(null)
    gridApiRef.current?.applyColumnState({ defaultState: { sort: null } })
    setHasChanges(false)
    setHasActiveFilter(false)
    setHasActiveSort(false)
    setActionMessage(t('tables.tableReset'))
  }

  function handleDeleteColumn() {
    if (!gridApiRef.current) return
    const focusedCell = gridApiRef.current.getFocusedCell()
    if (!focusedCell) {
      notify({ text: t('tables.noColumnSelected'), isSuccess: false })
      return
    }
    const colId = focusedCell.column.getColId()
    if (columnDefs.length <= 1) {
      notify({ text: t('tables.cannotDeleteLastColumn'), isSuccess: false })
      return
    }
    pushUndo()
    setColumnDefs((prev) => prev.filter((c) => c.field !== colId))
    setRowData((prev) =>
      prev.map((row) => {
        const next = { ...row }
        delete next[colId]
        return next
      })
    )
    setHasChanges(true)
    setFocusedColId(null)
    setActionMessage(t('tables.columnDeleted'))
  }

  function handleUndo() {
    const prev = undoStackRef.current.pop()
    if (!prev) return
    applyValues(prev)
    // The bottom of the undo stack is the original loaded state, so an empty
    // stack means we're back to the original — no serialized comparison needed.
    const hasRemaining = undoStackRef.current.length > 0
    setHasChanges(hasRemaining)
    setCanUndo(hasRemaining)
    setActionMessage(t('tables.undone'))
  }

  useEffect(() => {
    if (!gridContainerRef.current) {
      return
    }

    // Dialog unmounts/remounts the grid container — detect the new element and recreate the grid.
    if (gridApiRef.current && gridContainerElRef.current !== gridContainerRef.current) {
      gridApiRef.current.destroy()
      gridApiRef.current = null
    }

    if (!gridApiRef.current) {
      gridContainerElRef.current = gridContainerRef.current
      gridApiRef.current = createGrid<Record<string, string>>(gridContainerRef.current, {
        columnDefs,
        rowData,
        popupParent: gridContainerRef.current,
        defaultColDef: {
          resizable: true,
          flex: 1,
          minWidth: 100,
          filter: 'agTextColumnFilter',
          floatingFilter: true,
          editable: true,
          filterParams: {
            maxNumConditions: 4,
            buttons: ['reset'],
            filterOptions: [
              'contains',
              'notContains',
              'equals',
              'notEqual',
              'startsWith',
              'endsWith',
              'blank',
              'notBlank',
            ],
            defaultOption: 'contains',
            /**
             * Custom textMatcher intercepts contains/notContains to support
             * smart numeric expression syntax (>5, <=3% etc).
             * blank/notBlank are handled by the grid itself — textMatcher is not called for them.
             */
            textMatcher: (params: {
              filterOption: string | null | undefined
              value: string
              filterText: string
            }) => {
              const { filterOption, value, filterText } = params
              if (!filterText) return true

              if (filterOption === 'contains') {
                return matchesSmartFilter(value, filterText)
              }

              const v = (value ?? '').toLowerCase()
              const f = filterText.toLowerCase()
              switch (filterOption) {
                case 'notContains':
                  return !matchesSmartFilter(value, filterText)
                case 'equals':
                  return v === f
                case 'notEqual':
                  return v !== f
                case 'startsWith':
                  return v.startsWith(f)
                case 'endsWith':
                  return v.endsWith(f)
                default:
                  return true
              }
            },
          },
        },
        rowDragManaged: true,
        rowDragEntireRow: true,
        rowSelection: { mode: 'multiRow', checkboxes: true, enableClickSelection: true },
        suppressMovableColumns: false,
        domLayout: 'autoHeight',
        onSelectionChanged: () => {
          const count = gridApiRef.current?.getSelectedRows().length ?? 0
          setSelectedCount(count)
          if (skipActionMessageClearRef.current) {
            skipActionMessageClearRef.current = false
          } else {
            setActionMessage(count > 0 ? t('tables.selectedRows', { count }) : '')
          }
        },
        onCellFocused: (e) => {
          const colId = typeof e.column === 'string' ? e.column : (e.column?.getColId() ?? null)
          setFocusedColId(colId)
        },
        onFilterChanged: () => {
          setHasActiveFilter(gridApiRef.current?.isAnyFilterPresent() ?? false)
        },
        onSortChanged: () => {
          setHasActiveSort(
            gridApiRef.current?.getColumnState().some((col) => col.sort != null) ?? false
          )
        },
        onRowDragEnd: syncFromGrid,
        onCellValueChanged: handleCellValueChanged,
        onColumnMoved: () => {
          if (!gridApiRef.current) return
          undoStackRef.current.push(snapshotLive())
          setCanUndo(true)
          const newColOrder = gridApiRef.current
            .getAllDisplayedColumns()
            .map((col) => col.getColId())
          skipGridSyncRef.current = true
          setColumnDefs((prev) => {
            const colMap = new Map(prev.map((c) => [c.field, c]))
            return newColOrder
              .map((id) => colMap.get(id))
              .filter((c): c is ColDef => c !== undefined)
          })
          setHasChanges(true)
        },
      })

      return
    }

    if (skipGridSyncRef.current) {
      skipGridSyncRef.current = false
      return
    }

    gridApiRef.current.setGridOption('columnDefs', columnDefs)
    gridApiRef.current.setGridOption('rowData', rowData)
  }, [columnDefs, rowData, syncFromGrid, handleCellValueChanged, t])

  useEffect(() => {
    return () => {
      gridApiRef.current?.destroy()
      gridApiRef.current = null
    }
  }, [])

  async function insertFilteredTableToDocument() {
    if (!gridApiRef.current || columnDefs.length === 0) {
      const message = t('tables.noGridData')
      setActionMessage(message)
      notify({ text: message, isSuccess: false })
      return
    }

    const rows: string[][] = []
    gridApiRef.current.forEachNodeAfterFilterAndSort((node) => {
      const row = columnDefs.map((column) => {
        const field = column.field ?? ''
        return node.data?.[field] ?? ''
      })
      rows.push(row)
    })

    if (rows.length === 0) {
      const message = t('tables.noRowsAfterFilter')
      setActionMessage(message)
      notify({ text: message, isSuccess: false })
      return
    }

    const headerRow = columnDefs.map((column) => column.headerName ?? column.field ?? '')
    const tableData = [headerRow, ...rows]

    try {
      // Always insert as HTML — original markup when available, otherwise rebuilt from the
      // filtered grid data — via the shared insertHtml word service.
      const html = buildTableInsertHtml({
        tableData,
        sourceTableHtml,
        title: tableTitle,
        footer: tableFooter,
      })
      await insertHtml(html, { location: Word.InsertLocation.after })

      const message = t('tables.insertedStatus', { rows: rows.length })
      setActionMessage(message)
      notify({ text: message, isSuccess: true })
    } catch (error) {
      const fallbackMessage = t('tables.insertError')
      const message = error instanceof Error ? error.message : fallbackMessage
      setActionMessage(message)
      notify({ text: message, isSuccess: false })
    }
  }

  return {
    gridContainerRef,
    canInsert: columnDefs.length > 0 && rowData.length > 0,
    canUndo,
    hasChanges,
    canReset: hasChanges || hasActiveFilter || hasActiveSort,
    canDeleteRows: selectedCount > 0,
    canDeleteColumn: focusedColId !== null && columnDefs.some((col) => col.field === focusedColId),
    tableTitle,
    tableFooter,
    statusMessage: actionMessage,
    isTableDataLoading,
    isTableDataError:
      isTableDataError ||
      (!isTableDataLoading && filepoolTableData !== undefined && tableFilterValues === null),
    updateTableTitle: (value: string) => {
      setTableTitle(value)
      setHasChanges(true)
    },
    updateTableFooter: (value: string) => {
      setTableFooter(value)
      setHasChanges(true)
    },
    handleUndo,
    handleResetTable,
    handleDeleteSelected,
    handleDeleteColumn,
    insertFilteredTableToDocument,
  }
}
